import { Cancel, Check, Close } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useMemo } from "react";

import { formatSuggestionLabel } from "./helpers";
import { useClientAddressAutoComplete } from "./useClientAddressAutoComplete";

export default function ClientAddressAutoComplete({
  address,
  setAddress,
  inputRequired,
  defaultAddress,
  inputSx = {},
  hideLabel = false,
  containerClassNames = "w-full my-4",
  showAddressDetails = true,
}) {
  const {
    searchVal,
    suggestions,
    loading,
    helperText,
    textFieldSx,
    shouldShowSuggestions,
    canShowAddressDetails,
    showNoResults,
    manualAddressChange,
    handleSuggestionSelect,
    handleSearch,
    clearAddressSelection,
    clearSearch,
    updateExistingAddress,
    validAddressChange,
    isAuthorized,
    isError,
    smartyError,
    showAddressAdded,
    validationResponse,
  } = useClientAddressAutoComplete({
    address,
    setAddress,
    defaultAddress,
    inputSx,
    inputRequired,
    showAddressDetails,
  });

  const endAdornment = useMemo(() => {
    if (loading) {
      return <CircularProgress color="secondary" />;
    }
    if (showAddressAdded) {
      return (
        <Tooltip title="Address Added" className="pr-3">
          <InputAdornment position="end">
            <Check className="text-green-600" />
          </InputAdornment>
        </Tooltip>
      );
    }
    if (validationResponse && isError) {
      return (
        <InputAdornment position="end">
          <Cancel className="text-red-600" />
        </InputAdornment>
      );
    }
    return null;
  }, [isError, loading, showAddressAdded, validationResponse]);

  return (
    <div className="relative w-full">
      <div className={containerClassNames}>
        <div className="flex items-center">
          <TextField
            sx={textFieldSx}
            required={Boolean(inputRequired)}
            className="w-full"
            label={hideLabel ? "" : "Address"}
            value={address ? address.raw : searchVal}
            onChange={(e) => handleSearch(e.target.value)}
            error={isError || !isAuthorized || smartyError}
            disabled={!isAuthorized}
            helperText={helperText}
            InputProps={{
              endAdornment,
            }}
          />

          {!address ? (
            ""
          ) : (
            <Tooltip title="Clear selection" placement="top">
              <IconButton onClick={clearAddressSelection}>
                <Close />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {!shouldShowSuggestions ? (
          ""
        ) : (
          <List className="absolute z-10 max-h-[50vh] w-full overflow-y-auto rounded-b-md bg-white p-0 shadow-lg">
            {suggestions.map((suggestion, index) => (
              <ListItemButton
                key={index}
                onClick={() => {
                  handleSuggestionSelect(suggestion);
                }}
              >
                {formatSuggestionLabel(suggestion)}
              </ListItemButton>
            ))}
          </List>
        )}
        {!showNoResults ? (
          ""
        ) : (
          <List className="absolute z-10 max-h-[50vh] w-full overflow-y-auto rounded-b-md bg-white p-0 shadow-lg">
            <ListItem className="flex-col justify-center">
              No results found
              <button id="clear-btn" onClick={clearSearch} className="text-sky-600 underline">
                Clear Search
              </button>
            </ListItem>
          </List>
        )}
        {!canShowAddressDetails ? (
          ""
        ) : (
          <div className="my-4 w-full">
            <div className="flex w-full">
              <TextField
                required={Boolean(inputRequired)}
                className="mr-4 w-full"
                label="Suite Type - Apartment, suite, etc"
                value={!address.suite_type ? "" : address.suite_type}
                onChange={(e) => manualAddressChange(e.target.value, "suite_type", address)}
              />
              <TextField
                required={Boolean(inputRequired)}
                className="w-full"
                label="Suite Number - ex: 123"
                value={!address.suite_number ? "" : address.suite_number}
                onChange={(e) => manualAddressChange(e.target.value, "suite_number", address)}
              />
            </div>
            <Tooltip title={"To update City, State or Zip Code, search new address"}>
              <div className="my-4 flex w-full">
                <TextField
                  required={Boolean(inputRequired)}
                  className="mr-4 w-full"
                  label="City"
                  disabled
                  value={!address.city ? "" : address.city}
                  onChange={(e) => manualAddressChange(e.target.value, "city", address)}
                />
                <TextField
                  required={Boolean(inputRequired)}
                  className="mr-2 w-full"
                  label="State"
                  value={!address.state ? "" : address.state}
                  disabled
                  onChange={(e) => manualAddressChange(e.target.value, "state", address)}
                />
                <TextField
                  required={Boolean(inputRequired)}
                  className="w-full"
                  label="Zip Code"
                  value={!address.post_code ? "" : address.post_code}
                  disabled
                  onChange={(e) => manualAddressChange(e.target.value, "post_code", address)}
                />
              </div>
            </Tooltip>
            {validAddressChange ? (
              <div className="mx-auto w-fit">
                <Button
                  size="small"
                  onClick={updateExistingAddress}
                  variant="contained"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Update Validated Address
                </Button>
              </div>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
    </div>
  );
}
